import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import PageHeader from '@/components/PageHeader.jsx';
import { fetchCreditoCliente } from '@/services/clientes.js';

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(value || 0));

const statusStyles = {
  OK: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  EN_RIESGO: 'bg-amber-100 text-amber-800 border-amber-200',
  EXCEDIDO: 'bg-rose-100 text-rose-800 border-rose-200',
  MOROSO: 'bg-rose-100 text-rose-800 border-rose-200',
  BLOQUEADO: 'bg-rose-100 text-rose-800 border-rose-200',
};

export default function CreditoCliente() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [sucursalId, setSucursalId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await fetchCreditoCliente(id, {
          sucursalId: sucursalId || null,
        });
        if (mounted) setData(response);
      } catch {
        if (mounted) setError('No se pudo cargar el crédito del cliente.');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (id) {
      load();
    }

    return () => {
      mounted = false;
    };
  }, [id, sucursalId]);

  const clienteNombre = data?.cliente?.razonSocial || data?.cliente?.nombreFantasia || 'Cliente';
  const estadoCredito = data?.estado_credito || 'BLOQUEADO';
  const estadoClass = statusStyles[estadoCredito] || statusStyles.BLOQUEADO;

  const resumen = useMemo(
    () => [
      { label: 'Límite de crédito', value: formatCurrency(data?.limite_credito) },
      { label: 'Exposure', value: formatCurrency(data?.exposure) },
      { label: 'Saldo CC', value: formatCurrency(data?.saldo_actual) },
      { label: 'Saldo minutas', value: formatCurrency(data?.saldo_minutas) },
      { label: 'Deuda vencida', value: formatCurrency(data?.deuda_vencida ?? data?.saldo_vencido) },
      {
        label: 'Días crédito / tolerancia',
        value: `${Number(data?.dias_credito ?? data?.dias_mora_permitidos ?? 0)} / ${Number(data?.tolerancia_mora_dias ?? 0)}`,
      },
    ],
    [data],
  );

  const bloqueos = data?.bloqueos || [];
  const warnings = data?.warnings || [];
  const sucursales = data?.sucursales || [];

  return (
    <div className="min-h-screen bg-[rgba(var(--bg))]">
      <div className="max-w-[1400px] mx-auto px-4 py-6">
        <PageHeader title={`Crédito de ${clienteNombre}`} subtitle="Estado crediticio integrado con cuenta corriente y minutas." />

        <div className="flex flex-wrap items-center gap-4 mb-6">
          <span
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${estadoClass}`}
          >
            Estado crediticio: {estadoCredito}
          </span>
          <div className="flex items-center gap-2">
            <label className="text-xs font-semibold uppercase text-[rgb(var(--muted))]">Sucursal</label>
            <select
              value={sucursalId}
              onChange={(event) => setSucursalId(event.target.value)}
              className="rounded-xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] px-3 py-2 text-sm text-[rgb(var(--fg))]"
            >
              <option value="">Todas</option>
              {sucursales.map((sucursal) => (
                <option key={sucursal.id} value={sucursal.id}>
                  {sucursal.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-6 text-sm text-[rgb(var(--muted))]">
            Cargando estado crediticio...
          </div>
        ) : error ? (
          <div className="rounded-2xl border border-[rgb(var(--danger))] bg-[rgba(var(--danger),0.1)] p-6 text-sm text-[rgb(var(--danger))]">{error}</div>
        ) : (
          <>
            <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 mb-6">
              {resumen.map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] shadow-[var(--shadow)] p-4"
                >
                  <p className="text-xs uppercase tracking-wide text-[rgb(var(--muted))]">{item.label}</p>
                  <p className="mt-2 text-lg font-semibold text-[rgb(var(--fg))]">{item.value}</p>
                </div>
              ))}
            </section>

            <div className="mb-4 rounded-2xl border border-[rgb(var(--border))] bg-[rgba(var(--panel))] p-4 text-sm text-[rgb(var(--fg))]">
              Exposure: <strong>{formatCurrency(data?.exposure)}</strong> · % del límite: <strong>{Number(data?.pct_limite || 0).toFixed(2)}%</strong>
            </div>

            {bloqueos.length > 0 && (
              <div className="mb-4 rounded-2xl border border-[rgb(var(--danger))] bg-[rgba(var(--danger),0.1)] p-4 text-sm text-[rgb(var(--danger))]">
                <p className="font-semibold">Operación bloqueada</p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {bloqueos.map((item) => (
                    <li key={item.code}>{item.message}</li>
                  ))}
                </ul>
              </div>
            )}

            {warnings.length > 0 && (
              <div className="rounded-2xl border border-[rgb(var(--warning))] bg-[rgba(var(--warning),0.1)] p-4 text-sm text-[rgb(var(--warning))]">
                <p className="font-semibold">Advertencias</p>
                <ul className="mt-2 list-disc pl-5 space-y-1">
                  {warnings.map((item) => (
                    <li key={item.code}>{item.message}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
